/**
 * Clear controls for the two search inputs on the catalog surfaces.
 *
 * The client asked for a Clear button immediately right of Search. There are two Search buttons on
 * these pages and they belong to different owners, so this file covers both:
 *
 *  1. SEARCH PILL. Theme markup (sections/search-bar.liquid on the collection template,
 *     sections/collection-search-page.liquid on the search template). On the search template a term
 *     is always present, so Liquid renders Clear as a plain link and this file leaves it alone —
 *     it keeps working with JS off. On the collection template the box starts empty, so the button
 *     ships `hidden` and is revealed here on the first keystroke. A Clear that clears nothing is a
 *     dead control; revealing it on demand is what keeps it honest.
 *
 *  2. VEHICLE FINDER. The MAKE / MODEL / CHASSIS / PRODUCT TYPE panel is rendered by the Globo
 *     Smart Filter YMM app block, so there is no Liquid to edit — the button is injected next to
 *     the app's own submit button, exactly as assets/mex-catalog-toolbar.js drives the app's sort
 *     menu. The app renders late and re-renders on its own, hence the observer.
 *
 * Neither control touches the Search path. Submitting still goes through the app's / the theme's
 * own form, unchanged.
 */
(() => {
  if (window.__mexSearchClear) return;
  window.__mexSearchClear = true;

  const ROW = ".mex-search-row";
  const PILL_CLEAR = "[data-mex-search-clear]";
  const YMM = ".gf-YMM-forms";
  const YMM_SUBMIT = "button.spf-btn";
  const YMM_SELECT = "select.gf-form-select";
  const INJECTED = "data-mex-ymm-clear";

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ── search pill ────────────────────────────────────────────────────────── */

  /**
   * Only the BUTTON form is ours to drive. The search template renders an anchor with the same
   * hook, and hiding that would remove a working link from a page that always has a term.
   */
  const wirePill = () => {
    $$(ROW).forEach((row) => {
      const control = $(PILL_CLEAR, row);
      const input = $('input[name="q"]', row);
      if (!control || !input || control.tagName !== "BUTTON") return;
      if (control.dataset.mexWired === "1") return;
      control.dataset.mexWired = "1";

      const sync = () => {
        control.hidden = input.value.trim().length === 0;
      };

      control.addEventListener("click", () => {
        input.value = "";
        // The filter app's autocomplete is bound to this input; an input event lets it drop its
        // suggestion list instead of leaving a stale one open over the page.
        input.dispatchEvent(new Event("input", { bubbles: true }));
        sync();
        input.focus();
      });

      input.addEventListener("input", sync);
      input.addEventListener("change", sync);
      sync();
    });
  };

  /* ── vehicle finder ─────────────────────────────────────────────────────── */

  /** Params that describe the vehicle picker. Everything else on the URL is left alone. */
  const isVehicleParam = (key) => key.startsWith("car.");

  /**
   * Reset every picker, then let the app's own cascade run. The selects carry an inline
   * `onchange="toggleFormValue(id, this.value)"`, and an inline handler responds to a dispatched
   * event just as it does to a real one — so the app re-disables the dependent dropdowns itself
   * rather than this file reimplementing its cascade.
   */
  const resetPickers = (scope) => {
    const selects = $$(YMM_SELECT, scope);
    selects.forEach((select) => {
      select.value = "";
    });
    selects.forEach((select) => {
      select.dispatchEvent(new Event("change", { bubbles: true }));
    });
  };

  const clearVehicle = (scope) => {
    resetPickers(scope);

    const src = new URL(window.location.href);
    if (![...src.searchParams.keys()].some(isVehicleParam)) return; // nothing applied — stay put

    const out = new URL(src.pathname, src.origin);
    src.searchParams.forEach((value, key) => {
      if (!isVehicleParam(key)) out.searchParams.append(key, value);
    });
    window.location.assign(`${out.pathname}${out.search}`);
  };

  const wireVehicle = () => {
    $$(YMM).forEach((panel) => {
      const submit = $(YMM_SUBMIT, panel);
      if (!submit || $(`[${INJECTED}]`, panel)) return;

      const clear = document.createElement("button");
      clear.type = "button";
      // Deliberately NOT .spf-btn. The app's Search button is painted with !important from two
      // places (assets/main.css sets border:none!important, its background is !important too), so
      // borrowing that class means a Clear that can only be restyled by out-importanting the theme.
      // Instead the box metrics are copied off the real Search button — which keeps the two the
      // same size even if the app restyles them — and the paint comes from our own class.
      clear.className = "mex-ymm-clear";
      clear.setAttribute(INJECTED, "");
      const model = getComputedStyle(submit);
      ["height", "padding", "borderRadius", "fontSize", "fontWeight", "lineHeight", "letterSpacing", "textTransform"].forEach(
        (prop) => {
          clear.style[prop] = model[prop];
        }
      );
      // Borrow the label from the theme's own Clear control rather than hardcoding English or
      // adding a JS globals entry. It renders on both templates that carry this panel and Liquid
      // has already translated it, so the injected button follows the shopper's locale for free.
      clear.textContent = $(PILL_CLEAR)?.textContent.trim() || "Clear";
      clear.addEventListener("click", () => clearVehicle(panel));

      submit.insertAdjacentElement("afterend", clear);
    });
  };

  /* ── boot ───────────────────────────────────────────────────────────────── */

  const boot = () => {
    wirePill();
    wireVehicle();
  };

  /**
   * The app block paints after us and can replace its own panel, so watch for it rather than
   * guessing a delay. The observer stays cheap — it only reacts when a YMM panel without our
   * button is on the page — and it is the same survival strategy the toolbar bridge uses.
   */
  const observe = () => {
    const observer = new MutationObserver(() => {
      if ($(YMM) && !$(`[${INJECTED}]`)) wireVehicle();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  window.addEventListener("load", boot);
  document.addEventListener("collection:rerendered", boot);
  if (document.body) observe();
  else document.addEventListener("DOMContentLoaded", observe);
})();
